import { Dialog } from "primereact/dialog";
import { Checkbox } from "primereact/checkbox";
import { useState, useEffect, useRef, useCallback } from "react";
import { useAuthStore } from "~/store/authStore";
import { setScopedItem } from "~/store/workspaceStore";
import { getChartPreferenceKey } from "~/workspace/constants";
import marketdata from "~/data/Services/marketdata";
import { router } from "~/data/Services/ordersRouter";
import { settings } from "~/data/Services/settings";
import "./ChartSelectionModal.css";
import showcaseConfig from "./showcaseVideos.json";
const SHOWCASE_VIDEOS = showcaseConfig.videos;

export const ChartSelectionModal = ({ visible, onDismiss, authToken }) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [showDeprecationWarning, setShowDeprecationWarning] = useState(false);
  const [activeVideoIndex, setActiveVideoIndex] = useState(0);
  const videoRef = useRef(null);

  const getBroker = useAuthStore((state) => state.getBroker);
  const getUsername = useAuthStore((state) => state.getUsername);
  const broker = getBroker();
  const isRicoBroker = broker === "RICO";

  // Reset state when modal opens/closes
  useEffect(() => {
    if (visible) {
      setActiveVideoIndex(0);
    } else {
      setShowDeprecationWarning(false);
    }
  }, [visible]);

  const handleVideoEnded = useCallback(() => {
    setActiveVideoIndex((prev) => (prev + 1) % SHOWCASE_VIDEOS.length);
  }, []);

  const handleBadgeClick = useCallback((index) => {
    setActiveVideoIndex(index);
  }, []);

  // Load new video source when activeVideoIndex changes
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
      videoRef.current.play().catch(() => {});
    }
  }, [activeVideoIndex]);

  const handleDeprecatedChartClick = () => {
    setShowDeprecationWarning(true);
  };

  const handleContinueToOldChart = () => {
    setShowDeprecationWarning(false);
    redirectToHTML5();
  };

  const shouldShowLegacyChart = () => {
    const username = getUsername();
    const currentBroker = broker;
    const isInForceList = settings.ForceLegacyChart?.some(
      (entry) => entry.broker === currentBroker && entry.login === username,
    );
    if (isInForceList) return true;
    return settings.EnableLegacyChart === true;
  };

  const handleStayInTradeZoneCharts26 = () => {
    if (dontShowAgain) {
      setScopedItem(getChartPreferenceKey, "tradezone-charts-26");
    }
    onDismiss();
  };

  const redirectToHTML5 = () => {
    window.location.href = `https://www.tradezone.com.br/x2?auth=${encodeURIComponent(authToken)}`;
  };

  const redirectToSilverlight = () => {
    console.log(
      "[ChartSelection] Disconnecting from marketdata and router before Silverlight redirect",
    );
    try {
      if (marketdata.state === "online") {
        console.log("[ChartSelection] Stopping marketdata connection...");
        marketdata.stop();
      }
      if (router.state === "online") {
        console.log("[ChartSelection] Stopping router connection...");
        router.stop();
      }
    } catch (error) {
      console.error("[ChartSelection] Error disconnecting services:", error);
    }

    let cleanToken = authToken;
    const suffixes = ["1exx", "9ddhr", "0dhac"];
    for (const suffix of suffixes) {
      const index = authToken.lastIndexOf(suffix);
      if (index !== -1) {
        cleanToken = authToken.substring(0, index);
        break;
      }
    }
    window.location.href = `/Oob?auth=${encodeURIComponent(cleanToken)}&st=&symbol=IBOV&isOverturned=true`;
  };

  const redirectToTradingView = () => {
    let cleanToken = authToken;
    const ricoSuffixIndex = authToken.lastIndexOf("9ddhr");
    if (ricoSuffixIndex !== -1) {
      cleanToken = authToken.substring(0, ricoSuffixIndex);
    }
    const params = new URLSearchParams();
    params.append("force", "true");
    params.append("auth2", "Rkmlksd");
    params.append("auth", cleanToken);
    window.location.href = `https://tv2.tradezone.com.br/?${params.toString()}`;
  };

  return (
    <Dialog
      visible={visible}
      modal
      closable={false}
      className="chart-selection-modal"
      data-evo-id="chart-selection-modal"
      style={{ width: "92vw", maxWidth: "800px" }}
      header={
        <div className="modal-header-custom">
          <i
            className="pi pi-chart-line"
            style={{ fontSize: "1.1rem", marginRight: "0.375rem" }}
          />
          Bem-vindo ao TradeZone Charts 26!
        </div>
      }
    >
      {/* Charts26 — full width showcase */}
      <div
        className="charts26-showcase"
        data-evo-id="chartselection-card-tradezone-charts26"
      >
        <div className="showcase-video">
          <div className="badge-new">NOVO</div>
          <video
            ref={videoRef}
            className="preview-image"
            autoPlay
            muted
            playsInline
            onClick={handleStayInTradeZoneCharts26}
            onEnded={handleVideoEnded}
            poster="/assets/images/tradezone-charts-26-preview.jpg"
          >
            <source
              src={SHOWCASE_VIDEOS[activeVideoIndex].webm}
              type="video/webm"
            />
            <source
              src={SHOWCASE_VIDEOS[activeVideoIndex].mp4}
              type="video/mp4"
            />
          </video>
        </div>

        <div className="showcase-features">
          {SHOWCASE_VIDEOS.map((video, index) => (
            <span
              key={index}
              className={`feature-badge ${index === activeVideoIndex ? "feature-badge-active" : ""}`}
              onClick={() => handleBadgeClick(index)}
            >
              {video.icon ? (
                <i className={`codicon ${video.icon}`} />
              ) : (
                video.emoji
              )}{" "}
              {video.title}
            </span>
          ))}
          <a
            href="/whats-new.html"
            target="_blank"
            rel="noopener noreferrer"
            className="whats-new-link"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              window.open("/whats-new.html", "_blank");
            }}
            data-evo-id="chart-selection-whats-new-link"
          >
            <i className="pi pi-sparkles" />O que há de novo?
          </a>
        </div>
      </div>

      {/* Footer */}
      <div className="modal-footer-custom">
        <div className="footer-top-row">
          <div className="preference-area">
            <div className="preference-checkbox">
              <Checkbox
                inputId="dont-show"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.checked)}
              />
              <label htmlFor="dont-show">
                Não mostrar novamente (apenas TradeZone Charts 26)
              </label>
            </div>
            <small className="preference-note">
              Nota: Esta opção se aplica apenas ao TradeZone Charts 26. Gráficos
              legados sempre mostrarão este modal. Você pode alterar esta
              preferência depois no menu Configurações (botão navbar) →
              Preferências de Gráfico.
            </small>
          </div>
          <div className="footer-actions">
            <div />
            <button
              className="btn-use-charts26"
              onClick={handleStayInTradeZoneCharts26}
              data-evo-id="chartselection-btn-stay-charts26"
            >
              Usar Charts 26
            </button>
            <div className="footer-legacy-links">
              <button
                className="legacy-link"
                onClick={redirectToSilverlight}
                data-evo-id="chartselection-card-silverlight"
              >
                <i className="pi pi-desktop" />
                Silverlight (Windows)
              </button>
              {isRicoBroker && (
                <button
                  className="legacy-link"
                  onClick={redirectToTradingView}
                  data-evo-id="chartselection-card-tradingview"
                >
                  <i className="pi pi-external-link" />
                  TradingView
                </button>
              )}
              {shouldShowLegacyChart() && (
                <button
                  className="legacy-link deprecated-link"
                  onClick={handleDeprecatedChartClick}
                  data-evo-id="chartselection-card-html5-classic"
                >
                  <i className="pi pi-history" />
                  HTML5 Clássico
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Fullscreen deprecation warning */}
      {showDeprecationWarning && (
        <div className="end-date-fullscreen">
          <div className="end-date-fullscreen-content">
            <i className="pi pi-exclamation-triangle" />
            <div className="end-date-fullscreen-text">
              <span className="end-label">
                O gráfico HTML5 Clássico encerra em
              </span>
              <span className="end-date">30 Jan 2026</span>
              <span className="end-cta">Utilize o novo Charts 26</span>
            </div>
            <div className="warning-buttons">
              <button
                className="warning-btn warning-btn-secondary"
                onClick={handleContinueToOldChart}
                data-evo-id="chartselection-btn-continue-old-chart"
              >
                Continuar mesmo assim
              </button>
              <button
                className="warning-btn warning-btn-primary"
                onClick={() => {
                  setShowDeprecationWarning(false);
                  handleStayInTradeZoneCharts26();
                }}
                data-evo-id="chartselection-btn-stay-charts26"
              >
                Voltar e usar Charts 26
              </button>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
};
